import React from 'react';
import { Link } from 'react-router-dom';
import useSEO from '../hooks/useSEO';

export default function OmOs() {
  useSEO(
    'Om os | Chokolenten ApS',
    'Mød de to søstre bag Chokolenten i Hanstholm og hør historien om vores passion for håndlavet chokolade, Thy Kukies, kager og godt håndværk.'
  );

  return (
    <div className="pb-20">
      
      {/* Hero Section */}
      <section className="relative py-24 bg-choc-dark text-cream-light overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/2c62fa2f-a0e0-45c6-84dc-1b9d3147e5ca/m-ma-splader.jpg"
            alt="Om os baggrund"
            className="w-full h-full object-cover object-top opacity-50 scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-choc-dark/60 via-choc-dark/30 to-transparent" />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center flex flex-col items-center gap-4">
          <span className="text-xs font-sans uppercase tracking-[0.25em] text-berry-light font-bold">
            Vores historie
          </span>
          <h1 className="text-4xl sm:text-5xl font-serif font-bold text-cream-light tracking-wide">
            Historien om Chokolenten
          </h1>
          <p className="max-w-2xl text-sm sm:text-base text-cream-light/80 leading-relaxed font-sans">
            Mød de to søstre bag chokoladen og hør, hvordan en fælles drøm og en dyb kærlighed til det søde håndværk blev til Chokolenten ApS i Hanstholm.
          </p>
        </div>
      </section>

      {/* Historien Sektion */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
          
          {/* Venstre: Billede */}
          <div className="lg:col-span-5 relative">
            <div className="absolute -inset-4 bg-berry-accent/5 rounded-3xl rotate-1" />
            <img
              src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/2c62fa2f-a0e0-45c6-84dc-1b9d3147e5ca/imgi-156-675947616-1562812425847534-2913590839545059458-n.jpg"
              alt="De to søstre i Chokolentens butik"
              className="relative rounded-2xl border border-choc-dark/10 shadow-lg w-full object-cover aspect-[4/5]"
            />
          </div>

          {/* Højre: Tekst */}
          <div className="lg:col-span-7 flex flex-col gap-6">
            <span className="text-xs font-sans uppercase tracking-[0.2em] text-berry-accent font-bold">
              To søstre, én drøm
            </span>
            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-choc-dark leading-tight">
              Hvordan det hele startede
            </h2>
            <p className="text-base text-choc-dark/80 leading-relaxed font-sans">
              Chokolenten blev etableret i 2017 af Alice Bisgaard. Det begyndte som en lille, hyggelig butik med chokolade, gaveartikler og spiritus. Med tiden voksede den lille chokoladeproduktion, og i 2020 rykkede Chokolenten derfor i større lokaler. Samme år faldt Alice helt tilfældigt over Stinne på torvet. Få dage senere var elevkontrakten underskrevet – og chokoladeeventyret begyndte.
            </p>
            <p className="text-base text-choc-dark/80 leading-relaxed font-sans italic bg-berry-accent/5 p-4 rounded-xl border-l-4 border-berry-accent">
              “14 dage inde i elevtiden kiggede Alice på mig og sagde: Forresten, så er du jo kun elev, fordi du skal overtage Chokolenten, når du er færdig.” Sådan blev fremtiden ligesom lagt lidt på plads!
            </p>
            <p className="text-base text-choc-dark/80 leading-relaxed font-sans">
              Stinne har det, som mange nok ville kalde en popcornhjerne. Der er masser af idéer – gerne flere på én gang – og nogle gange kan de hurtigt løbe lidt løbsk. Derfor var der brug for en makker, som kunne holde overblikket … og måske også holde lidt styr på Stinne. 😉 Og her kommer Frederikke ind i billedet. For hvem er bedre til at holde styr på Stinne end hendes egen storesøster?
            </p>
            <p className="text-base text-choc-dark/80 leading-relaxed font-sans">
              Frederikke skulle dog lige overtales. Hun ville egentlig gerne hjem til Thy, men tanken om at blive selvstændig sammen med sin lillesøster var ikke ligefrem drømmen fra starten. Da Stinnes elevtid nærmede sig sin afslutning, begyndte snakken dog at blive mere seriøs. Frederikke rykkede hjem til Thy, og i sommeren 2023 blev papirerne underskrevet. Den 1. november 2023 åbnede vi dørene til Chokolenten med os to søstre bag roret – og et chokoladeeventyr, der for alvor var vores eget.
            </p>
            <p className="text-base text-choc-dark/80 leading-relaxed font-sans">
              Vi har haft fokus på at bevare Chokolentens kerne og kærligheden til det gode håndværk, samtidig med at vi udvikler nye produkter, udforsker idéer og sætter vores eget præg på chokoladeeventyret. Vi producerer blandt andet Thy Kukies, Thy Cups, fyldte chokolader, påskeæg og meget mere. Vores håndlavede lækkerier finder ikke kun vej til hylderne i vores egen butik, men også til en række skønne specialforretninger rundt omkring i landet. Alt sammen håndlavet med masser af kærlighed – lige her i hjertet af Thy, i Hanstholm.
            </p>
          </div>

        </div>
      </section>

      {/* Vores Værdier Sektion */}
      <section className="bg-cream-light py-20 border-y border-choc-dark/5 mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col gap-12">
          <div className="text-center max-w-2xl mx-auto flex flex-col gap-4">
            <span className="text-xs font-sans uppercase tracking-[0.2em] text-berry-accent font-bold">
              Hvad vi står for
            </span>
            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-choc-dark">
              Vores kerneværdier
            </h2>
            <p className="text-sm sm:text-base text-choc-dark/70 font-sans">
              Hver eneste dag arbejder vi ud fra tre enkle principper, som sikrer, at du altid får den bedste oplevelse hos os.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Værdi 1 */}
            <div className="bg-cream-bg p-8 rounded-2xl border border-choc-dark/10 shadow-sm flex flex-col gap-4 text-center items-center">
              <div className="w-14 h-14 rounded-full bg-berry-accent/10 flex items-center justify-center text-berry-accent">
                <i className="fa-solid fa-hand text-2xl" />
              </div>
              <h3 className="font-serif font-bold text-xl text-choc-dark">
                100% Håndlavet
              </h3>
              <p className="text-sm text-choc-dark/70 font-sans leading-relaxed">
                Vi rører, bager, støber og pakker alt i hånden. Det giver vores produkter personlighed og sikrer en helt særlig kvalitet, man kan smage.
              </p>
            </div>

            {/* Værdi 2 */}
            <div className="bg-cream-bg p-8 rounded-2xl border border-choc-dark/10 shadow-sm flex flex-col gap-4 text-center items-center">
              <div className="w-14 h-14 rounded-full bg-berry-accent/10 flex items-center justify-center text-berry-accent">
                <i className="fa-solid fa-award text-2xl" />
              </div>
              <h3 className="font-serif font-bold text-xl text-choc-dark">
                Mod på at skabe nyt
              </h3>
              <p className="text-sm text-choc-dark/70 font-sans leading-relaxed">
                Gode idéer opstår, når man tør tænke ud af boksen. Vi elsker at eksperimentere, udvikle nye favoritter og føre kreative idéer ud i livet. Fra de første tanker til færdige lækkerier.
              </p>
            </div>

            {/* Værdi 3 */}
            <div className="bg-cream-bg p-8 rounded-2xl border border-choc-dark/10 shadow-sm flex flex-col gap-4 text-center items-center">
              <div className="w-14 h-14 rounded-full bg-berry-accent/10 flex items-center justify-center text-berry-accent">
                <i className="fa-solid fa-heart text-2xl" />
              </div>
              <h3 className="font-serif font-bold text-xl text-choc-dark">
                Lokal kærlighed
              </h3>
              <p className="text-sm text-choc-dark/70 font-sans leading-relaxed">
                Vi elsker Hanstholm og Thy! Vi er stolte af at være en lokal butik, og vi støtter op om det lokale erhvervsliv og vores fantastiske kunder.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Sektion */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mt-24 text-center flex flex-col items-center gap-6">
        <h2 className="text-3xl sm:text-4xl font-serif font-bold text-choc-dark">
          Lyst til noget sødt?
        </h2>
        <p className="max-w-2xl text-sm sm:text-base text-choc-dark/70 leading-relaxed font-sans">
          Kig forbi butikken på Bytorvet 30 i Hanstholm og se vores friske udvalg i montren, eller tag et kig på vores produkter her på hjemmesiden.
        </p>
        <div className="flex flex-col sm:flex-row items-center gap-4 mt-2 w-full sm:w-auto">
          <Link
            to="/produkter/chokolade"
            className="w-full sm:w-auto bg-berry-accent hover:bg-berry-accent/90 text-white font-sans font-semibold px-8 py-4 rounded-xl transition-colors shadow-md cursor-pointer"
          >
            Se vores udvalg
          </Link>
          <Link
            to="/kontakt"
            className="w-full sm:w-auto bg-transparent hover:bg-choc-dark/5 text-choc-dark border border-choc-dark/20 font-sans font-semibold px-8 py-4 rounded-xl transition-colors cursor-pointer"
          >
            Kontakt os
          </Link>
        </div>
      </section>

    </div>
  );
}
